import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';

import PageHeader from 'components/ui/page-header';
import SeletorDeCiclo from 'components/ui/seletor-de-ciclo';
import { AuthContext } from 'context/AuthContext';
import { useCicloDaTela } from 'hooks/useCicloDaTela';
import { DestaqueDoFunci } from 'services/destaques.service';
import { ciclosDeFechamento } from 'utils/ciclos-de-fechamento';

import './styles.scss';
import ListaDeDestaques from './components/ListaDeDestaques';
import PerfilNosDestaques from './components/PerfilNosDestaques';
import { nomeExibido } from './destaques-modelo';

/**
 * Destaques (`Galeria.dc.html`): a lista dos funcis do prefixo com destaque no ciclo e o perfil de cada um. O
 * perfil abre em `/destaques?funci=<matrícula>&ciclo=<_id>` (DST-20); a lista fica montada por baixo, para voltar
 * na mesma página e com a mesma busca.
 */
function PaginaDeDestaques() {
  const { realCycle, userInfo } = useContext(AuthContext);
  const [parametros, setParametros] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const titulo = useRef<HTMLHeadingElement>(null);

  const ciclos = useMemo(() => ciclosDeFechamento(realCycle), [realCycle]);
  // CIC-10, CIC-11: o ciclo da lista é o ciclo do app. Fora dos ciclos de fechamento (o 2S2026, aberto), o mais
  // recente deles, sem aviso e sem mudar o ciclo do app (CIC-16, pedido da homologação).
  const { ciclo: cicloDoApp, cicloExibido: cicloAtivo, trocar } = useCicloDaTela(ciclos);
  const [nomeDoFunci, setNomeDoFunci] = useState<string>();

  const matricula = parametros.get('funci') ?? undefined;
  const cicloDaUrl = parametros.get('ciclo') ?? undefined;
  const cicloDoPerfil = ciclos.find(ciclo => ciclo._id === cicloDaUrl) ?? cicloAtivo;

  // Perfil e ciclo do app andam juntos. Aberto por um endereço, o ciclo do endereço passa a ser o do app, antes da
  // pintura (sem mostrar outro ciclo de passagem); depois disso, trocar o ciclo no menu leva o endereço junto.
  const [perfilSincronizado, setPerfilSincronizado] = useState(false);
  const aberturaPelaLista = !!location.state?.daLista;
  // Aberto por um endereço, a lista só monta depois de o ciclo do endereço virar o do app: antes, ela leria o ciclo
  // de passagem (o recuo de CIC-16) só para ser descartada. Aberto pela lista, ela continua montada por baixo.
  const listaMontada = !matricula || aberturaPelaLista || perfilSincronizado;
  const cicloDaUrlNoRecorte = ciclos.some(ciclo => ciclo._id === cicloDaUrl);
  const entradaAplicada = useRef<string>();
  useLayoutEffect(() => {
    // Uma vez por entrada (funci e ciclo do endereço): o endereço só muda depois seguindo o ciclo do app.
    const entrada = matricula && cicloDaUrl && cicloDaUrlNoRecorte ? `${matricula}|${cicloDaUrl}` : undefined;
    if (entrada === entradaAplicada.current) return;
    entradaAplicada.current = entrada;
    setPerfilSincronizado(false);
    // Um endereço (link compartilhado) leva o menu ao ciclo dele, seja qual for o ciclo do app. Aberto pela lista,
    // o ciclo é o que a lista já mostra, e o do app fica como está, mesmo no recuo de CIC-16.
    if (entrada && cicloDaUrl && cicloDaUrl !== cicloDoApp?._id && !aberturaPelaLista) trocar(cicloDaUrl);
  }, [matricula, cicloDaUrl, cicloDaUrlNoRecorte, cicloDoApp, aberturaPelaLista, trocar]);

  useEffect(() => {
    if (!matricula) return;
    if (!perfilSincronizado) {
      if (!cicloDaUrlNoRecorte || cicloAtivo?._id === cicloDaUrl) setPerfilSincronizado(true);
      return;
    }
    if (cicloAtivo && cicloAtivo._id !== cicloDaUrl) {
      // A troca do endereço feita aqui não é uma entrada nova: o ciclo do app fica como está (CIC-18).
      entradaAplicada.current = `${matricula}|${cicloAtivo._id}`;
      setParametros({ funci: matricula, ciclo: cicloAtivo._id }, { replace: true, state: location.state });
    }
    // O endereço segue o ciclo do app; `location.state` (veio da lista?) vai junto, para o voltar seguir igual.
  }, [matricula, cicloAtivo, cicloDaUrl, cicloDaUrlNoRecorte, perfilSincronizado, setParametros, location.state]);

  // Trocar entre a lista e o perfil leva a tela ao topo e o foco ao título (WCAG 2.4.3); na entrada, não.
  const primeiraVista = useRef(true);
  useEffect(() => {
    setNomeDoFunci(undefined);
    if (primeiraVista.current) {
      primeiraVista.current = false;
      return;
    }
    window.scrollTo(0, 0);
    titulo.current?.focus();
  }, [matricula]);

  // Estável: o perfil a usa na carga, e uma função nova a cada render releria o funci.
  const mostrarNomeDoFunci = useCallback((funci: DestaqueDoFunci) => setNomeDoFunci(nomeExibido(funci)), []);

  function abrir(funci: DestaqueDoFunci) {
    if (cicloAtivo) setParametros({ funci: funci._id, ciclo: cicloAtivo._id }, { state: { daLista: true } });
  }

  // Aberto pela lista, volta no histórico (como o voltar do navegador); aberto por um endereço, troca pela lista.
  function voltar() {
    if (location.state?.daLista) navigate(-1);
    else setParametros({}, { replace: true });
  }

  const subtitulo = matricula ? nomeDoFunci : cicloAtivo && `Ciclo ${cicloAtivo.title}`;

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 pb-10 leading-[normal] lg:px-0">
      <PageHeader
        ref={titulo}
        titulo="Destaques"
        voltar={matricula ? { onClick: voltar, destino: 'Destaques' } : { to: '/', destino: 'a página inicial' }}
        selo={
          subtitulo && (
            <>
              {/* Separador decorativo: fica na cor do design (1,76:1), escondido do leitor de tela. No celular o
                  subtítulo quebra para a linha de baixo, e o separador sumiria sozinho no fim da linha. */}
              <span aria-hidden="true" className="hidden font-text text-[22px] text-line sm:inline">
                |
              </span>
              <span className="font-display text-[22px] font-bold text-brand-blue">{subtitulo}</span>
            </>
          )
        }
        acoes={
          // No perfil, o seletor da tela some: o ciclo segue pelo menu.
          <div hidden={!!matricula} className="w-[240px] max-w-full">
            <SeletorDeCiclo ciclos={ciclos} valor={cicloAtivo?._id} onChange={trocar} />
          </div>
        }
      />

      <div hidden={!!matricula}>
        {cicloAtivo && listaMontada && (
          <ListaDeDestaques
            key={cicloAtivo._id}
            ciclo={{ id: cicloAtivo._id, titulo: cicloAtivo.title }}
            onAbrir={abrir}
            prefixoDeQuemAcessa={{ prefixo: userInfo?.prefixo, dependencia: userInfo?.nome_dependencia }}
          />
        )}
      </div>

      {matricula && cicloDoPerfil && (
        <PerfilNosDestaques
          key={`${cicloDoPerfil._id}-${matricula}`}
          ciclo={{
            id: cicloDoPerfil._id,
            titulo: cicloDoPerfil.title,
            inicio: cicloDoPerfil.date_start,
            fim: cicloDoPerfil.date_end,
          }}
          matricula={matricula}
          onVoltar={voltar}
          onCarregado={mostrarNomeDoFunci}
        />
      )}

      <Outlet />
    </div>
  );
}

/**
 * "Destaques" no menu abre a seção do começo (DST-28, HML-18): a lista, sem perfil e sem busca. Cada navegação nova
 * para a lista (PUSH ou REPLACE, sem `funci`) troca a chave e remonta a página. Abrir o perfil não remonta, e
 * voltar dele pelo histórico (POP) também não: a lista volta como estava.
 */
export default function Destaque() {
  const location = useLocation();
  const tipoDeNavegacao = useNavigationType();
  const [chaveAtual, setChaveAtual] = useState(location.key);
  const [ultimaLocalizacao, setUltimaLocalizacao] = useState(location.key);

  // Estado ajustado no render (o padrão do React para derivar da prop anterior), e não num efeito: a remontagem
  // acontece já na navegação, sem um quadro intermediário, e o render não altera ref.
  const naLista = !new URLSearchParams(location.search).has('funci');
  if (location.key !== ultimaLocalizacao) {
    setUltimaLocalizacao(location.key);
    if (naLista && tipoDeNavegacao !== 'POP') setChaveAtual(location.key);
  }

  const primeiraEntrada = useRef(true);
  useEffect(() => {
    if (primeiraEntrada.current) {
      primeiraEntrada.current = false;
      return;
    }
    window.scrollTo(0, 0);
  }, [chaveAtual]);

  return <PaginaDeDestaques key={chaveAtual} />;
}
